import GraphRect from "./graph_rect";
import GraphContainer, { getOrSet } from "./graph_container";
import GraphGroup from "./graph_group";
import createTextFilter from "./create-text-filter";

class GraphNumberline extends GraphRect {
    _showAxisNumbers;
    _showAxisX;
    _showAxisY;
    _colorText;
    _colorTextShadow;

    constructor(xax, yax, xrange, yrange) {
        super();

        this._axes = Object.freeze({ x: xax, y: yax });
        this._ranges = Object.freeze({ x: xrange, y: yrange });

        const { feFlood, filter } = createTextFilter(this._uuid);

        this._feFlood = feFlood;
        this._filter = filter;

        this._defs.append(this._filter);

        this._lines = new GraphGroup()
            .setAttribute("name", "grid-lines")
            .setAttribute("clip-path", `url(#clip_${this._uuid})`);

        this.append(this._lines);

        this._step = 1;
        this._stepMinor = 0.25;
    }

    /**
     * @returns {this}
     */
    step() { return getOrSet.call(this, this, "_step", ...arguments); }

    /**
     * @returns {this}
     */
    stepMinor() { return getOrSet.call(this, this, "_stepMinor", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisNumbers() { return getOrSet.call(this, this, "_showAxisNumbers", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisX() { return getOrSet.call(this, this, "_showAxisX", ...arguments); }

    /**
     * @returns {this}
     */
    showAxisY() { return getOrSet.call(this, this, "_showAxisY", ...arguments); }

    /**
     * @returns {this}
     */
    colorText() { return getOrSet.call(this, this, "_colorText", ...arguments); }

    /**
     * @returns {this}
     */
    colorTextShadow() { return getOrSet.call(this, this, "_colorTextShadow", ...arguments); }

    redraw() {
        super.redraw();

        this._feFlood.setAttribute("flood-color", this._colorTextShadow);

        this._lines.erase();

        const xrange = this._ranges.x, yrange = this._ranges.y;
        const step = this._step, stepMinor = this._stepMinor;
        const xfun = this._axes.x.value, yfun = this._axes.y.value;

        const toString = val => {
            if (1e-2 <= Math.abs(val) && Math.abs(val) <= 1e3 || Math.abs(val) < 1e-10) return val % 1 === 0
                ? val
                : parseFloat(val.toFixed(2));
            return val.toExponential(2);
        };

        const xFits = xrange.x <= 0 && xrange.y >= 0, yFits = yrange.x <= 0 && yrange.y >= 0;
        if (this._showAxisNumbers && this._showAxisX && xFits && this._showAxisY && yFits) {
            const xval = THREE.Math.mapLinear(0, xrange.x, xrange.y, 0, this._size.width);
            const yval = THREE.Math.mapLinear(0, yrange.x, yrange.y, 0, this._size.height);

            this._lines.append(
                GraphContainer
                    .create("text")
                    .setAttribute("pointer-events", "none")
                    .setAttribute("font-size", "16px")
                    .setAttribute("dominant-baseline", "text-before-edge")
                    .setAttribute("text-anchor", "end")
                    .setAttribute("fill", this._colorText)
                    .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                    .setAttribute("x", this._origin.x + xval - 8)
                    .setAttribute("y", this._origin.y + yval + 5)
                    .innerText(0)
            );
        }

        if (this._showAxisNumbers && this._showAxisX) {
            const yval = THREE.Math.mapLinear(0, yrange.x, yrange.y, 0, this._size.height);
            const labelCount = (xrange.y - xrange.x) / step;
            const fits = Math.max(1, Math.floor(labelCount / 10));

            for (let i = xrange.x - (xrange.x % step); i <= xrange.y; i += step * fits) {
                const xval = THREE.Math.mapLinear(xfun(i), xrange.x, xrange.y, 0, this._size.width);

                if (Math.abs(i) >= 1e-6 || (!this._showAxisY && xFits)) {
                    this._lines.append(
                        GraphContainer
                            .create("text")
                            .setAttribute("pointer-events", "none")
                            .setAttribute("font-size", "16px")
                            .setAttribute("dominant-baseline", "text-before-edge")
                            .setAttribute("text-anchor", "middle")
                            .setAttribute("fill", this._colorText)
                            .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                            .setAttribute("x", this._origin.x + xval)
                            .setAttribute("y", this._origin.y + yval + 5)
                            .innerText(toString(i))
                    );
                }
            }
        }

        if (this._showAxisNumbers && this._showAxisY) {
            const xval = THREE.Math.mapLinear(0, xrange.x, xrange.y, 0, this._size.width);
            const labelCount = (yrange.y - yrange.x) / step;
            const fits = Math.max(1, Math.floor(labelCount / 10));

            for (let i = yrange.x - (yrange.x % step); i <= yrange.y; i += step * fits) {
                const yval = THREE.Math.mapLinear(yfun(i), yrange.x, yrange.y, 0, this._size.height);

                if (Math.abs(i) >= 1e-6 || (!this._showAxisX && yFits)) {
                    this._lines.append(
                        GraphContainer
                            .create("text")
                            .setAttribute("pointer-events", "none")
                            .setAttribute("font-size", "16px")
                            .setAttribute("dominant-baseline", "central")
                            .setAttribute("text-anchor", "end")
                            .setAttribute("fill", this._colorText)
                            .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                            .setAttribute("x", this._origin.x + xval - 8)
                            .setAttribute("y", this._origin.y + yval)
                            .innerText(toString(-i))
                    );
                }
            }
        }

        return this;
    }
}

export default GraphNumberline;
export { GraphNumberline };